import { useState, useRef, useEffect, type CSSProperties } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Sparkles, Bell, X, Send, Loader2, Users, Bot } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useChat } from "@/hooks/useChat";
import { useNotifications, type Notification } from "@/hooks/useNotifications";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

function getInitials(name?: string | null) {
  return (
    name
      ?.split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("") || "?"
  );
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-assistant`;

const popupStyle: CSSProperties = {
  position: "fixed",
  bottom: "80px",
  right: "24px",
  width: "320px",
  height: "500px",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#ffffff",
  borderRadius: "16px",
  border: "1px solid #e2e8f0",
  boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
  zIndex: 9999,
  overflow: "hidden",
};

const headerStyle: CSSProperties = {
  flexShrink: 0,
  padding: "12px 16px",
  borderBottom: "1px solid #e5e7eb",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "inherit",
};

const tabsStyle: CSSProperties = {
  flexShrink: 0,
  display: "flex",
  borderBottom: "1px solid #e5e7eb",
  backgroundColor: "inherit",
};

const contentStyle: CSSProperties = {
  flex: 1,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
};

const messagesStyle: CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "12px",
  minHeight: 0,
};

const footerStyle: CSSProperties = {
  flexShrink: 0,
  padding: "8px 12px",
  borderTop: "1px solid #e5e7eb",
  display: "flex",
  gap: "8px",
};

function getTabStyle(active: boolean): CSSProperties {
  return {
    flex: 1,
    border: "none",
    backgroundColor: active ? "#f5f3ff" : "transparent",
    color: active ? "#7c3aed" : "#4b5563",
    padding: "10px 8px",
    fontSize: "13px",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    cursor: "pointer",
  };
}

function getBubbleStyle(role: Message["role"]): CSSProperties {
  return {
    maxWidth: "85%",
    borderRadius: "12px",
    padding: "10px 12px",
    fontSize: "14px",
    lineHeight: 1.45,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    backgroundColor: role === "user" ? "#7c3aed" : "#f3f4f6",
    color: role === "user" ? "#ffffff" : "#111827",
  };
}

export function GlobalChatPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"ai" | "chat" | "notifications">("ai");
  const [input, setInput] = useState("");
  const [teamInput, setTeamInput] = useState("");
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [aiMessages, setAiMessages] = useState<Message[]>([
    {
      id: "1",
      role: "assistant",
      content: "Olá! Como posso ajudar você hoje? Posso gerar ideias, roteiros ou copies para seus clientes.",
      timestamp: new Date(),
    },
  ]);
  const aiMessagesEndRef = useRef<HTMLDivElement>(null);
  const teamMessagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { channels, channelsLoading } = useChat(undefined);
  const { notifications, unreadCount, isLoading: notificationsLoading, markAsRead } = useNotifications();
  const unreadNotifications = notifications.filter((notification) => !notification.read_at && !notification.is_read);
  const defaultTeamChannel = channels.find((channel) => channel.channel_type === "general") ?? channels[0] ?? null;
  const activeTeamChannelId = selectedChannelId || defaultTeamChannel?.id || undefined;
  const {
    messages: activeChannelMessages,
    messagesLoading: activeChannelMessagesLoading,
    sendMessage: sendTeamMessage,
  } = useChat(activeTeamChannelId);
  const teamMessages = activeChannelMessages.slice(-20);
  const selectedTeamChannel = channels.find((channel) => channel.id === activeTeamChannelId) ?? null;

  useEffect(() => {
    if (!channels.length) {
      setSelectedChannelId(null);
      return;
    }

    if (!selectedChannelId || channels.every((channel) => channel.id !== selectedChannelId)) {
      setSelectedChannelId(defaultTeamChannel?.id || channels[0].id);
    }
  }, [channels, defaultTeamChannel?.id, selectedChannelId]);

  useEffect(() => {
    aiMessagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [aiMessages]);

  useEffect(() => {
    teamMessagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [teamMessages, activeTab]);

  const streamChat = async (userMessages: { role: string; content: string }[]) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.access_token) {
      throw new Error("Sessão inválida");
    }

    const response = await fetch(CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({
        messages: userMessages,
        tone: "profissional",
        model: "gdc",
        clientContext: null,
        contentType: null,
      }),
    });

    if (!response.ok) {
      if (response.status === 429) {
        toast({
          title: "Limite excedido",
          description: "Muitas requisições. Aguarde alguns segundos.",
          variant: "destructive",
        });
        throw new Error("Rate limit");
      }

      if (response.status === 402) {
        toast({
          title: "Créditos insuficientes",
          description: "Adicione créditos em Settings > Workspace > Usage",
          variant: "destructive",
        });
        throw new Error("Payment required");
      }

      throw new Error("Failed to get response");
    }

    if (!response.body) {
      throw new Error("No response body");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let textBuffer = "";
    let assistantContent = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      textBuffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
        let line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);

        if (line.endsWith("\r")) line = line.slice(0, -1);
        if (line.startsWith(":") || line.trim() === "") continue;
        if (!line.startsWith("data: ")) continue;

        const jsonStr = line.slice(6).trim();
        if (jsonStr === "[DONE]") break;

        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content;

          if (content) {
            assistantContent += content;
            setAiMessages((prev) => {
              const last = prev[prev.length - 1];

              if (last?.role === "assistant" && prev.length > 1) {
                return prev.map((message, index) =>
                  index === prev.length - 1 ? { ...message, content: assistantContent } : message,
                );
              }

              return [
                ...prev,
                {
                  id: crypto.randomUUID(),
                  role: "assistant",
                  content: assistantContent,
                  timestamp: new Date(),
                },
              ];
            });
          }
        } catch {
          textBuffer = line + "\n" + textBuffer;
          break;
        }
      }
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content: input,
      timestamp: new Date(),
    };

    setAiMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const chatMessages = [...aiMessages, userMessage]
        .filter((message) => message.id !== "1")
        .map((message) => ({ role: message.role, content: message.content }));

      await streamChat(chatMessages);
    } catch (error) {
      console.error("Chat error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTeamSend = async () => {
    if (!teamInput.trim() || sendTeamMessage.isPending || !activeTeamChannelId) return;

    try {
      await sendTeamMessage.mutateAsync({ content: teamInput.trim() });
      setTeamInput("");
    } catch (error) {
      console.error("Team chat error:", error);
    }
  };

  return (
    <>
      <style>{`
        html.dark .global-chat-popup-shell {
          background-color: #1e1e2e !important;
        }
      `}</style>
      {!isOpen ? (
        <motion.button
          onClick={() => setIsOpen(true)}
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            zIndex: 9999,
            width: "56px",
            height: "56px",
            borderRadius: "9999px",
            border: "none",
            backgroundColor: "#7c3aed",
            color: "#ffffff",
            boxShadow: "0 10px 30px rgba(124, 58, 237, 0.35)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <MessageCircle className="h-6 w-6" />
          <span className="sr-only">Abrir chat</span>
        </motion.button>
      ) : (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="global-chat-popup-shell"
            style={popupStyle}
          >
            <div style={headerStyle}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f5f3ff",
                    color: "#7c3aed",
                  }}
                >
                  <Sparkles className="h-4 w-4" />
                </div>
                <span style={{ fontSize: "15px", fontWeight: 600, color: "#111827" }}>Assistente</span>
              </div>

              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div style={tabsStyle}>
              <button type="button" onClick={() => setActiveTab("ai")} style={getTabStyle(activeTab === "ai")}>
                <Bot className="h-4 w-4" />
                IA
              </button>
              <button type="button" onClick={() => setActiveTab("chat")} style={getTabStyle(activeTab === "chat")}>
                <Users className="h-4 w-4" />
                Equipe
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("notifications")}
                style={getTabStyle(activeTab === "notifications")}
              >
                <Bell className="h-4 w-4" />
                <span>Notificações</span>
                {unreadCount > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                    {unreadCount}
                  </Badge>
                )}
              </button>
            </div>

            {activeTab === "ai" && (
              <div style={contentStyle}>
                <div style={messagesStyle}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {aiMessages.map((message) => (
                      <div
                        key={message.id}
                        style={{
                          display: "flex",
                          justifyContent: message.role === "user" ? "flex-end" : "flex-start",
                        }}
                      >
                        <div style={getBubbleStyle(message.role)}>{message.content}</div>
                      </div>
                    ))}

                    {isLoading && aiMessages[aiMessages.length - 1]?.role !== "assistant" && (
                      <div style={{ display: "flex", justifyContent: "flex-start" }}>
                        <div style={{ ...getBubbleStyle("assistant"), padding: "10px 12px" }}>
                          <Loader2 className="h-4 w-4 animate-spin text-accent" />
                        </div>
                      </div>
                    )}

                    <div ref={aiMessagesEndRef} />
                  </div>
                </div>

                <div style={footerStyle}>
                  <Textarea
                    placeholder="Digite sua mensagem..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    className="min-h-[40px] max-h-[100px] resize-none text-sm"
                    disabled={isLoading}
                    style={{ flex: 1 }}
                  />
                  <Button
                    size="icon"
                    className="h-10 w-10 bg-accent text-accent-foreground hover:bg-accent/90"
                    onClick={handleSend}
                    disabled={!input.trim() || isLoading}
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}

            {activeTab === "chat" && (
              <div style={contentStyle}>
                {channelsLoading || (activeTeamChannelId && activeChannelMessagesLoading) ? (
                  <div style={{ ...messagesStyle, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Loader2 className="h-5 w-5 animate-spin text-accent" />
                  </div>
                ) : !selectedTeamChannel ? (
                  <div style={{ ...messagesStyle, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ textAlign: "center" }}>
                      <Users className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
                      <h4 style={{ marginBottom: "4px", fontSize: "15px", fontWeight: 600, color: "#111827" }}>
                        Sem canal disponível
                      </h4>
                      <p style={{ fontSize: "14px", color: "#6b7280" }}>
                        Nenhum canal foi encontrado para o workspace atual.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        flexShrink: 0,
                        padding: "10px 12px",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      <select
                        value={activeTeamChannelId}
                        onChange={(e) => setSelectedChannelId(e.target.value)}
                        style={{
                          width: "100%",
                          flexShrink: 0,
                          border: "1px solid #d1d5db",
                          borderRadius: "10px",
                          padding: "10px 12px",
                          fontSize: "14px",
                          color: "#111827",
                          backgroundColor: "#ffffff",
                        }}
                      >
                        {channels.map((channel) => (
                          <option key={channel.id} value={channel.id}>
                            {channel.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={messagesStyle}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {teamMessages.length === 0 ? (
                          <div style={{ display: "flex", minHeight: "220px", alignItems: "center", justifyContent: "center" }}>
                            <p style={{ fontSize: "14px", color: "#6b7280" }}>
                              Nenhuma mensagem ainda neste canal.
                            </p>
                          </div>
                        ) : (
                          teamMessages.map((message) => (
                            <div key={message.id} style={{ display: "flex", gap: "12px" }}>
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={message.profile?.avatar_url || undefined} />
                                <AvatarFallback className="bg-accent/10 text-[11px] text-accent">
                                  {getInitials(message.profile?.full_name)}
                                </AvatarFallback>
                              </Avatar>

                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                  <span style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>
                                    {message.profile?.full_name || "Usuário"}
                                  </span>
                                  {message.is_ai_response && (
                                    <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                                      IA
                                    </Badge>
                                  )}
                                </div>

                                <p
                                  style={{
                                    marginTop: "4px",
                                    whiteSpace: "pre-wrap",
                                    fontSize: "14px",
                                    color: "#111827",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {message.content}
                                </p>
                              </div>
                            </div>
                          ))
                        )}

                        <div ref={teamMessagesEndRef} />
                      </div>
                    </div>

                    <div style={footerStyle}>
                      <Textarea
                        placeholder="Enviar mensagem para a equipe..."
                        value={teamInput}
                        onChange={(e) => setTeamInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleTeamSend();
                          }
                        }}
                        className="min-h-[40px] max-h-[100px] resize-none text-sm"
                        disabled={sendTeamMessage.isPending}
                        style={{ flex: 1 }}
                      />
                      <Button
                        size="icon"
                        className="h-10 w-10 bg-accent text-accent-foreground hover:bg-accent/90"
                        onClick={handleTeamSend}
                        disabled={!teamInput.trim() || sendTeamMessage.isPending}
                      >
                        {sendTeamMessage.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === "notifications" && (
              <div style={contentStyle}>
                <div style={messagesStyle}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {notificationsLoading ? (
                      <div style={{ display: "flex", justifyContent: "center", paddingTop: "24px" }}>
                        <Loader2 className="h-5 w-5 animate-spin text-accent" />
                      </div>
                    ) : unreadNotifications.length === 0 ? (
                      <div style={{ display: "flex", minHeight: "220px", alignItems: "center", justifyContent: "center" }}>
                        <p style={{ fontSize: "14px", color: "#6b7280" }}>
                          Nenhuma notificação pendente.
                        </p>
                      </div>
                    ) : (
                      unreadNotifications.map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onDismiss={() => markAsRead.mutate(notification.id)}
                        />
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      )}
    </>
  );
}

function NotificationItem({
  notification,
  onDismiss,
}: {
  notification: Notification;
  onDismiss: () => void;
}) {
  const type = notification.type === "comment" || notification.type === "success" ? notification.type : "demand";
  const iconStyle =
    type === "demand"
      ? { backgroundColor: "#fef3c7", color: "#d97706" }
      : type === "comment"
        ? { backgroundColor: "#ede9fe", color: "#7c3aed" }
        : { backgroundColor: "#dcfce7", color: "#16a34a" };

  return (
    <div
      style={{
        padding: "12px",
        borderRadius: "12px",
        backgroundColor: "#f8fafc",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
        <div
          style={{
            ...iconStyle,
            width: "32px",
            height: "32px",
            borderRadius: "9999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {type === "demand" && <Bell className="h-4 w-4" />}
          {type === "comment" && <MessageCircle className="h-4 w-4" />}
          {type === "success" && <Sparkles className="h-4 w-4" />}
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>{notification.title}</p>
              <p style={{ marginTop: "2px", fontSize: "12px", color: "#6b7280" }}>
                {notification.message || "Sem detalhes adicionais."}
              </p>
              <p style={{ marginTop: "6px", fontSize: "12px", color: "#9ca3af" }}>
                {formatDistanceToNow(new Date(notification.created_at), {
                  addSuffix: true,
                  locale: ptBR,
                })}
              </p>
            </div>
            <button
              type="button"
              onClick={onDismiss}
              style={{
                border: "none",
                background: "transparent",
                color: "#9ca3af",
                cursor: "pointer",
                padding: 0,
                lineHeight: 0,
              }}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
